import React, { useEffect, useState } from 'react';
import Button from '@/componentsDS/Button/Button';
import { ButtonType, ButtonVariant } from '@/componentsDS/Button/Button.type';
import Dropdown from '@/componentsDS/Dropdown/Dropdown';
import Input from '@/componentsDS/Input/index';
import Icon from '@/componentsDS/Icon/Icon';
import { IconSize, IconStyle } from '@/componentsDS/Icon/Icon.type';
import PropTypes from 'prop-types';

const PaginationInfo = ({ totalItems, totalPages, currentPg, pgSize }) => {
	if (totalItems === 0) {
		return <>0 entries</>;
	}

	if (totalPages === 1) {
		return <>Showing {totalItems} entries</>;
	}

	const startEntry = (currentPg - 1) * pgSize + 1;
	const endEntry = Math.min(currentPg * pgSize, totalItems);
	return (
		<>
			Showing {startEntry}-{endEntry} of {totalItems} entries,&nbsp;
		</>
	);
};

const PaginationControls = ({ pagination, data, pageSize, currentPage, handlePreviousPage, handleNextPage, handlePageSizeChange, handlePageChange, paginationOptions }) => {
	const defaultPageSize = 10;
	const meta = pagination ? pagination?.meta : null;
	const totalItems = pagination ? (meta?.count ?? 0) : data.length;
	const totalPages = pagination ? (meta?.pageCount ?? 1) : Math.ceil(totalItems / pageSize);

	const currentPg = pagination ? (meta?.currentPage ?? 1) : (currentPage ?? 1);
	const pgSize = pagination ? (meta?.pageSize ?? defaultPageSize) : (pageSize ?? defaultPageSize);

	const [pageInputValue, setPageInputValue] = useState(currentPg.toString());

	useEffect(() => {
		setPageInputValue(currentPg.toString());
	}, [currentPg]);

	const handlePageInputSubmit = () => {
		const newPage = Number.parseInt(pageInputValue, 10);

		if (Number.isNaN(newPage) || newPage < 1 || newPage > totalPages) {
			setPageInputValue(currentPg.toString());
			return;
		}

		if (pagination) {
			pagination.onPageChange(newPage);
		} else {
			handlePageChange(newPage);
		}
	};

	// Debounce the page input submission
	useEffect(() => {
		const handler = setTimeout(() => {
			handlePageInputSubmit();
		}, 500);

		return () => {
			clearTimeout(handler);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageInputValue]);

	const options = paginationOptions ?? [
		{ id: 10, label: '10' },
		{ id: 20, label: '20' },
		{ id: 30, label: '30' }
	];

	const [selectedOption, setSelectedOption] = useState([{ id: pgSize, label: pgSize.toString() }]);

	useEffect(() => {
		const newSize = Number.parseInt(selectedOption[0].label, 10);

		if (pagination) {
			pagination.onPageSizeChange(newSize);
		} else {
			handlePageSizeChange(newSize);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedOption]);

	return (
		<div className="ds-table__pagination">
			<div className="ds-table__pagination-controls__left">
				<div className="ds-table__pagination-info">
					<PaginationInfo totalItems={totalItems} totalPages={totalPages} currentPg={currentPg} pgSize={pgSize} />
					<Dropdown
						setWidth={100}
						options={options}
						value={selectedOption}
						onAdd={setSelectedOption}
						multiple={false}
						trigger={
							<div className="ds-table__pagination-controls__left__dropdown__button">
								<Button type={ButtonType.UTILITY} variant={ButtonVariant.UTILITY} text={selectedOption[0].label} />
								<Icon name="chevron-down" size={IconSize.SMALL} style={IconStyle.SOLID} />
							</div>
						}
						size="default"
					/>
					entries per page
				</div>
			</div>
			{totalPages !== undefined && currentPg <= totalPages && totalPages > 1 && (
				<div className="ds-table__pagination-controls__right">
					<div className="ds-table__pagination-controls__input__wrapper">
						<div className="ds-table__pagination-controls__input">
							<Input
								id="pagination-input"
								name="pagination-input"
								placeholder=""
								value={pageInputValue}
								onChange={(e) => {
									// pitstop's DS Input forwards onChange to the native
									// element (admin-ui's emits the string value instead).
									const value = e.target.value.replace(/\D/g, '');
									setPageInputValue(value);
								}}
								disableClear={true}
								fullWidth={true}
								addClassName="ds-table__pagination-controls__input__field"
							/>
						</div>
						of {totalPages} pages
					</div>
					<div className="ds-table__pagination-controls__page-control">
						<Button type={ButtonType.UTILITY} variant={ButtonVariant.UTILITY} text={<Icon name="chevron-left" size={IconSize.MEDIUM} style={IconStyle.SOLID} />} onClick={pagination ? pagination.onPrevious : handlePreviousPage}  />

						<Button type={ButtonType.UTILITY} variant={ButtonVariant.UTILITY} text={<Icon name="chevron-right" size={IconSize.MEDIUM} style={IconStyle.SOLID} />} onClick={pagination ? pagination.onNext : handleNextPage}  />
					</div>
				</div>
			)}
		</div>
	);
};

export default PaginationControls;


PaginationInfo.propTypes = { 
	totalItems: PropTypes.number,
	totalPages: PropTypes.number,
	currentPg: PropTypes.number,
	pgSize: PropTypes.number
}

PaginationControls.propTypes = {
	pagination: PropTypes.object,
	data: PropTypes.array,
	pageSize: PropTypes.number,
	currentPage: PropTypes.number,
	handlePreviousPage: PropTypes.func,
	handleNextPage: PropTypes.func,
	handlePageSizeChange: PropTypes.func,
	handlePageChange: PropTypes.func,
	paginationOptions: PropTypes.array
}